import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { Client } from 'src/providers/client/client';
import { Genome } from 'src/providers/genome/genome';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';
import { OnboardingComponent } from 'src/shared/components/onboarding/onboarding';

@Component({
    selector: 'tidy-restocking-onboarding',
    templateUrl: './restocking-onboarding.component.html',
    styleUrls: ['./restocking-onboarding.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class RestockingOnboardingComponent implements OnInit {

  addedItems: any;
  addresses: any[] = [];
  categoryItems: any[] = [];
  form: UntypedFormGroup;
  itemForm: UntypedFormGroup;
  loaded: boolean;
  submitted: boolean;

  boundSaveItems = this.saveItems.bind(this);

  constructor(
    private client: Client,
    private fb: UntypedFormBuilder,
    private genome: Genome,
    private onboardingProvider: OnboardingProvider,
    private util: Util,
    private rightSidePanelService: RightSidePanelService,
    public windowService: WindowService
  ) {
    this.form = this.fb.group({
      whereToOrder: ['', Validators.required],
      whereToShip: ['', Validators.required]
    });
    this.itemForm = this.fb.group({
      itemName: ['', Validators.required],
      current: [''],
      min: [''],
      max: [''],
    });
  }

  async ngOnInit() {
    this.rightSidePanelService.setDialogPageTitle('Add Inventory');
    const [categories, addresses] = await Promise.all([
      this.genome.getCategories(),
      this.client.getAddresses()
    ]);
    this.addresses = addresses;
    await this.parseCategories(categories);
    this.loaded = true;
  }

  async parseCategories(categories) {
    this.addedItems = this.buildAddedItems(categories);
    this.categoryItems = this.addedItems.map((category) => ({
      viewValue: category.name,
      value: category.id,
      icon: category?.default_icon_url,
      current: category.current,
      min: category.min,
      max: category.max
    }));
  }

  buildAddedItems(categories) {
    let items = [
      { name: 'Toilet Paper', current: 40, min: 10, max: 60 },
      { name: 'Paper Towels', current: 20, min: 5, max: 30 },
      { name: 'Dish Soap', current: 3, min: 1, max: 5 },
      { name: 'Hand Soap', current: 6, min: 2, max: 8 },
      { name: 'Laundry Detergent', current: 40, min: 10, max: 60 },
      { name: 'Dishwasher Detergent', current: 30, min: 8, max: 45 },
      { name: 'Trash Bags', current: 40, min: 10, max: 60 },
      { name: 'Coffee', current: 5, min: 2, max: 8 },
      { name: 'Coffee Filters', current: 100, min: 20, max: 150 },
      { name: 'Shampoo', current: 6, min: 2, max: 10 },
      { name: 'Conditioner', current: 6, min: 2, max: 10 },
      { name: 'Body Wash', current: 6, min: 2, max: 10 }
    ];
    items.map((i) => {
      categories.map((c) => {
        if (i.name === c.name) {
          i['id'] = c.id;
          i['icon'] = c.default_icon_url;
        }
      });
    });
    items = items.filter((i) => i['id']);
    return items;
  }

  selectItemName(_value) {
    const category = this.categoryItems.find((c) => c.value === this.itemForm.value.itemName);
    this.itemForm.patchValue({
      current: category.current,
      min: category.min,
      max: category.max
    });
  }

  addItem(): void {
    this.submitted = true;
    if (!this.itemForm.valid) {
      return;
    }
    const category = this.categoryItems.find((c) => c.value === this.itemForm.value.itemName);
    this.addedItems.push({
      id: category.value,
      icon: category.icon,
      name: category.viewValue,
      current: this.itemForm.value.current,
      min: this.itemForm.value.min,
      max: this.itemForm.value.max
    })
    this.itemForm.reset();
    this.submitted = false;
  }

  removeItem(item: any): void {
    this.addedItems = this.addedItems.filter((i) =>
      !(i.name === item.name && i.current === item.current && i.min === item.min && i.max === item.max)
    );
  }

  async saveItems(): Promise<void> {
    try {
      if (this.itemForm.value.itemName) {
        this.addItem();
      }
      const payload = {
        address_ids: this.addresses.map((address) => address.id),
        room_objects: this.addedItems.map((item) => ({
          category_id: item.id,
          where_to_order: this.form.value.whereToOrder,
          restocking_notes: this.form.value.whereToShip,
          current_quantity: item.current,
          min_quantity: item.min,
          max_quantity: item.max,
          add_to_cleaning_checklist: item.addToChecklist,
        })),
      };
      await Promise.all([
        this.genome.createManyRoomObjects(payload),
        this.onboardingProvider.markGoalAsCompleted('inventoryAutomation'),
      ]);
      this.rightSidePanelService.setDialogPageTitle('Getting Started');
      this.rightSidePanelService.navigateTo('onboarding', {}, OnboardingComponent);
      this.onboardingProvider.setShowOnboardingInHeader(true);
      this.util.showSuccess('Items saved succesfully');
    } catch (error) {
      const errorMessage = error?.err?.message || 'Error saving items';
      this.util.showError(errorMessage);
    }
  }
}
